// When to ask the player what they think.
//
// A calibration exercise is worthless if the questions are asked at moments nobody cares about, and
// actively harmful if it interrupts. So the trigger rules are deliberately narrow: three kinds of
// moment, one question open at a time, a cooldown, and a hard cap per fight.
//
// The three moments are the ones the debrief already treats as decision points
// (`decisionPoints`, src/debrief/analyze.ts), which is what makes the forecasts scoreable against
// something the run already computed:
//
//   1. a significant contact in a named area of interest — the information requirement the player drew
//      the NAI to answer has just been answered, and "where is his main effort" is the next question;
//   2. the first sighting of a contact reported as a reserve-type vehicle — the moment "will he commit
//      it" becomes a live question rather than a hypothetical;
//   3. the lead element crossing a phase line — the moment the plan commits to a third, and the moment
//      a doctrinal enemy starts deciding.
//
// Pure functions over the event list, with the state the caller holds. No React, no store, no clock:
// `tests/ui/elicit.test.ts` drives them directly.

import type { Event, UnitType } from '../../sim/types';
import { TICKS_PER_MIN } from '../../sim/types';
import type { ElicitQuestionId } from '../../state/elicit';

/** Reported types that mean "this could be the reserve". The scripted red cell holds armor back
 * (`stepReserve`), so an armor report in depth is the sighting that makes the question live. */
const RESERVE_TYPES: ReadonlySet<string> = new Set<UnitType>(['armor']);

/** Sim minutes between questions. A player asked twice in three minutes stops reading the question. */
export const ELICIT_COOLDOWN_MIN = 8;

/** Questions per fight. Past this the exercise is a survey, not a decision aid. */
export const MAX_ELICITATIONS = 5;

export interface ElicitTrigger {
  eventId: number;
  t: number;
  question: ElicitQuestionId;
  /** The question as the player will read it. */
  prompt: string;
  /** For a choice question, the options the chips offer. */
  options?: string[];
  /** One line of why this moment: the event, in words. */
  because: string;
}

export interface TriggerState {
  /** Event ids already turned into a question. */
  asked: readonly number[];
  /** Tick of the most recent question, or null when none has been asked. */
  lastAskedTick: number | null;
  /** How many have been asked this fight. */
  count: number;
}

export const EMPTY_TRIGGER_STATE: TriggerState = { asked: [], lastAskedTick: null, count: 0 };

export interface TriggerOptions {
  /** The third names this corridor actually has, for the main-effort chips. */
  thirds: readonly string[];
  /** Current sim tick, so a trigger from ten minutes ago is not raised now. */
  tick: number;
  /** How stale a trigger may be before it is dropped, in sim minutes. */
  maxAgeMin?: number;
}

/** Whether an event is one of the three kinds that raise a question. Exported for the test. */
export function triggerKindOf(ev: Event): 'nai' | 'reserveSighting' | 'plCrossing' | null {
  if (ev.side !== 0) return null;
  if (ev.kind === 'nai.contact') return 'nai';
  if (ev.kind === 'pl.crossed') return 'plCrossing';
  if (ev.kind === 'contact.new' && ev.significant) {
    const typeConf = ev.payload.typeConf;
    if (typeof typeConf === 'string' && RESERVE_TYPES.has(typeConf)) return 'reserveSighting';
  }
  return null;
}

/**
 * The next question to ask, or null.
 *
 * Scans newest first: when two triggers land in the same batch of events the most recent one wins,
 * because the older one is about a picture the player has already moved past.
 */
export function nextTrigger(
  events: readonly Event[], state: TriggerState, opts: TriggerOptions,
): ElicitTrigger | null {
  if (state.count >= MAX_ELICITATIONS) return null;
  if (state.lastAskedTick !== null && opts.tick - state.lastAskedTick < ELICIT_COOLDOWN_MIN * TICKS_PER_MIN) {
    return null;
  }
  const maxAge = (opts.maxAgeMin ?? 3) * TICKS_PER_MIN;

  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (opts.tick - ev.t > maxAge) break; // the list is in time order; everything before this is older
    if (state.asked.includes(ev.id)) continue;
    const kind = triggerKindOf(ev);
    if (!kind) continue;

    if (kind === 'reserveSighting') {
      return {
        eventId: ev.id,
        t: ev.t,
        question: 'reserveCommitSoon',
        prompt: 'Will the enemy commit its reserve in the next 10 minutes?',
        because: 'First sighting of a contact reported as armour — this is the one he may be holding back.',
      };
    }

    if (kind === 'nai') {
      const name = typeof ev.payload.name === 'string' ? ev.payload.name : 'a named area of interest';
      return {
        eventId: ev.id,
        t: ev.t,
        question: 'mainEffortThird',
        prompt: 'Where is the enemy main effort?',
        options: [...opts.thirds],
        because: `Contact in ${name} — the information requirement you drew it for has just been answered.`,
      };
    }

    const plName = typeof ev.payload.name === 'string' ? ev.payload.name : 'a phase line';
    return {
      eventId: ev.id,
      t: ev.t,
      question: 'mainEffortThird',
      prompt: 'Where is the enemy main effort?',
      options: [...opts.thirds],
      because: `Your lead element crossed ${plName} — your plan has committed to a third, and so will his.`,
    };
  }

  return null;
}

/** Folds a raised question into the trigger state. */
export function afterAsking(state: TriggerState, trigger: ElicitTrigger, tick: number): TriggerState {
  return {
    asked: [...state.asked, trigger.eventId],
    lastAskedTick: tick,
    count: state.count + 1,
  };
}
